/**
 * Clerk wiring.
 *
 * Stage 3. Mounts `<ClerkProvider>` at the app root with the
 * publishable key from `PUBLIC_CLERK_PUBLISHABLE_KEY`. Routing uses
 * TanStack Router — Clerk's `routerPush` / `routerReplace` props are
 * wired to our `router.navigate` so Clerk-internal navigations
 * (e.g. `setActive({ redirectUrl })`) share the same router instance.
 *
 * See plans/dashboard-rewrite/essentials.md §3 for the `PUBLIC_*`
 * env prefix rationale.
 */

import { ClerkProvider } from '@clerk/react';
import { useRouter } from '@tanstack/react-router';
import type { PropsWithChildren, ReactElement } from 'react';

const publishableKey = import.meta.env.PUBLIC_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  // Hard fail in dev so a misconfigured `.env` shows up immediately
  // instead of surfacing as a confusing "Clerk is not initialised"
  // error deep inside a sign-in flow.
  throw new Error(
    'PUBLIC_CLERK_PUBLISHABLE_KEY is required. Copy apps/dashboard-v2/.env.example → .env and fill it in.',
  );
}

const validatedPublishableKey: string = publishableKey;

export function ClerkRootProvider({ children }: PropsWithChildren): ReactElement {
  const router = useRouter();

  return (
    <ClerkProvider
      publishableKey={validatedPublishableKey}
      routerPush={(to) => router.navigate({ to, replace: false })}
      routerReplace={(to) => router.navigate({ to, replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/onboarding"
      afterSignOutUrl="/sign-in"
    >
      {children}
    </ClerkProvider>
  );
}
